<template>
  <div v-if="product" class="product-item-page">
    <section class="hero-slab">
      <div class="hero-slab__media" :style="{ backgroundImage: 'url(' + product.image + ')' }"></div>
      <div class="hero-slab__overlay"></div>
      <div class="hero-slab__content container reveal">
        <div class="section-title">
          <p>{{ product.category }}</p>
          <h1>{{ product.title }}</h1>
        </div>
        <p>{{ product.summary }}</p>
      </div>
    </section>

    <page-trail
      :items="[
        { label: $t('common.home'), to: '/' },
        { label: $t('nav.products'), to: '/products' },
        { label: product.category, to: categoryPath },
        { label: product.code, to: $route.path }
      ]"
      :back-label="$t('common.backToList')"
      :back-to="categoryPath"
    />

    <section class="product-item-section light-panel">
      <div class="container product-item-layout reveal">
        <div class="product-item-media">
          <div v-if="productImages.length" class="product-gallery">
            <div ref="gallerySwiper" class="swiper product-gallery__swiper">
              <div class="swiper-wrapper">
                <button
                  v-for="(image, index) in productImages"
                  :key="image"
                  type="button"
                  class="swiper-slide product-gallery__slide"
                  @click="openPreview(index)"
                >
                  <img :src="image" :alt="product.title" loading="lazy">
                </button>
              </div>
              <button v-if="productImages.length > 1" ref="galleryPrev" class="product-gallery__nav product-gallery__nav--prev" type="button">{{ $t('common.previous') }}</button>
              <button v-if="productImages.length > 1" ref="galleryNext" class="product-gallery__nav product-gallery__nav--next" type="button">{{ $t('common.next') }}</button>
              <div v-if="productImages.length > 1" ref="galleryPagination" class="product-gallery__pagination"></div>
            </div>

            <div v-if="productImages.length > 1" class="product-gallery__thumbs">
              <button
                v-for="(image, index) in productImages"
                :key="image + '-thumb'"
                type="button"
                class="product-gallery__thumb"
                :class="{ 'is-active': activeImageIndex === index }"
                @click="goToImage(index)"
              >
                <img :src="image" :alt="product.title" loading="lazy">
              </button>
            </div>
          </div>
        </div>
        <div class="product-item-info">
          <div class="section-title">
            <p>{{ product.category }}</p>
            <h2>{{ product.code }}</h2>
          </div>
          <p>{{ product.summary }}</p>
          <h3 class="product-item-subtitle">{{ $t('caseDetail.specification') }}</h3>
          <div class="product-item-table">
            <div v-for="item in product.specs" :key="item.label">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </div>
          </div>
          <div class="product-item-actions">
            <router-link to="/contact?tab=quote" class="product-item-link">{{ $t('product.sendInquiry') }}</router-link>
            <router-link :to="categoryPath" class="product-item-link product-item-link--secondary">{{ $t('common.backToList') }}</router-link>
          </div>
        </div>
      </div>
    </section>

    <div v-if="previewOpen" class="product-preview" @click.self="closePreview">
      <button type="button" class="product-preview__close" @click="closePreview">{{ $t('common.close') }}</button>
      <button v-if="productImages.length > 1" type="button" class="product-preview__nav product-preview__nav--prev" @click="stepPreview(-1)">{{ $t('common.previous') }}</button>
      <img :src="productImages[previewIndex]" :alt="product.title">
      <button v-if="productImages.length > 1" type="button" class="product-preview__nav product-preview__nav--next" @click="stepPreview(1)">{{ $t('common.next') }}</button>
    </div>
  </div>
</template>

<script>
import Swiper from 'swiper'
import Navigation from 'swiper/modules/navigation.mjs'
import Pagination from 'swiper/modules/pagination.mjs'
import 'swiper/swiper.css'
import 'swiper/modules/navigation.css'
import 'swiper/modules/pagination.css'
import site from '../mock/strongova'
import PageTrail from '../components/PageTrail.vue'
import { fetchPublicProduct, normalizeProduct } from '../api/public'

const categoryMap = {
  Plank: '/products-list/plank',
  'Herringbone Plank 90°': '/products-list/herringbone-plank-90deg',
  'Chevron Plank 45°': '/products-list/chevron-plank-45deg',
  Parquet: '/products-list/parquet',
  'Oversized Plank': '/products-list/oversized-plank'
}

export default {
  name: 'ProductItemPage',
  components: {
    PageTrail
  },
  data () {
    return {
      backendProduct: null,
      loadingProduct: false,
      gallerySwiper: null,
      activeImageIndex: 0,
      previewOpen: false,
      previewIndex: 0
    }
  },
  computed: {
    product () {
      if (this.backendProduct) return this.backendProduct
      const mockProduct = site.productItems[this.$route.params.slug]
      return mockProduct ? normalizeProduct(mockProduct, site.hero.background) : null
    },
    categoryPath () {
      if (!this.product) return '/products'
      if (this.product.categorySlug) return '/products-list/' + this.product.categorySlug
      return categoryMap[this.product.category] || '/products'
    },
    productImages () {
      if (!this.product) return []
      const galleryImages = Array.isArray(this.product.gallery)
        ? this.product.gallery.filter(Boolean)
        : []
      const images = galleryImages.length ? galleryImages : [this.product.image].filter(Boolean)

      return Array.from(new Set(images))
    }
  },
  watch: {
    '$route.params.slug': {
      immediate: true,
      handler () {
        this.loadBackendProduct()
      }
    }
  },
  mounted () {
    this.initGallery()
    this.revealProductContent()
  },
  beforeDestroy () {
    this.destroyGallery()
  },
  methods: {
    loadBackendProduct () {
      this.loadingProduct = true
      this.backendProduct = null
      const routeSlug = this.$route.params.slug

      fetchPublicProduct(routeSlug)
        .then(product => {
          this.backendProduct = normalizeProduct(product, site.hero.background)
          this.activeImageIndex = 0
          this.previewOpen = false
          this.initGallery()
          this.revealProductContent()
        })
        .catch(() => {
          this.backendProduct = null
        })
        .then(() => {
          this.loadingProduct = false
          if (!this.product) {
            this.$router.replace('/products')
          }
        })
    },
    initGallery () {
      this.$nextTick(() => {
        this.destroyGallery()
        if (!this.$refs.gallerySwiper || this.productImages.length < 2) return

        this.gallerySwiper = new Swiper(this.$refs.gallerySwiper, {
          modules: [Navigation, Pagination],
          slidesPerView: 1,
          speed: 520,
          loop: false,
          navigation: {
            prevEl: this.$refs.galleryPrev,
            nextEl: this.$refs.galleryNext
          },
          pagination: {
            el: this.$refs.galleryPagination,
            clickable: true
          },
          on: {
            slideChange: swiper => {
              this.activeImageIndex = swiper.activeIndex
            }
          }
        })
      })
    },
    destroyGallery () {
      if (this.gallerySwiper) {
        this.gallerySwiper.destroy(true, true)
        this.gallerySwiper = null
      }
    },
    goToImage (index) {
      this.activeImageIndex = index
      if (this.gallerySwiper) {
        this.gallerySwiper.slideTo(index)
      }
    },
    openPreview (index) {
      this.previewIndex = index
      this.previewOpen = true
    },
    closePreview () {
      this.previewOpen = false
    },
    stepPreview (direction) {
      const total = this.productImages.length
      if (!total) return
      this.previewIndex = (this.previewIndex + direction + total) % total
    },
    revealProductContent () {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          Array.from(this.$el.querySelectorAll('.reveal')).forEach(node => {
            node.classList.add('is-visible')
          })
        })
      })
    }
  }
}
</script>

<style>
.product-item-section {
  padding: 84px 0 112px;
}

.product-item-page .hero-slab__content.reveal {
  transition-duration: 0.9s;
}

.product-item-page .product-item-layout.reveal {
  transition-delay: 0.08s;
  transition-duration: 0.82s;
}

.product-item-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(380px, 0.92fr);
  gap: 64px;
  align-items: start;
}

.product-item-media {
  background: #fff;
  border: 1px solid rgba(23, 20, 17, 0.1);
  min-width: 0;
}

.product-gallery {
  min-width: 0;
}

.product-gallery__swiper {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #fff;
}

.product-gallery__slide {
  width: 100%;
  display: block;
  padding: 0;
  border: 0;
  background: #fff;
  cursor: zoom-in;
}

.product-gallery__slide img {
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  display: block;
  object-fit: cover;
}

.product-gallery__nav {
  position: absolute;
  top: 50%;
  z-index: 2;
  min-width: 56px;
  height: 36px;
  transform: translateY(-50%);
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: rgba(23, 20, 17, 0.62);
  color: #fff;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.product-gallery__nav--prev {
  left: 14px;
}

.product-gallery__nav--next {
  right: 14px;
}

.product-gallery__pagination {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 14px;
  z-index: 2;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.product-gallery__pagination .swiper-pagination-bullet {
  width: 8px;
  height: 8px;
  margin: 0;
  background: rgba(255, 255, 255, 0.72);
  opacity: 1;
}

.product-gallery__pagination .swiper-pagination-bullet-active {
  background: #fff;
}

.product-gallery__thumbs {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  padding: 12px;
  border-top: 1px solid rgba(23, 20, 17, 0.08);
  background: #f7f4ef;
}

.product-gallery__thumb {
  padding: 0;
  border: 2px solid transparent;
  background: #fff;
  cursor: pointer;
}

.product-gallery__thumb.is-active {
  border-color: #171411;
}

.product-gallery__thumb img {
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  display: block;
  object-fit: cover;
}

.product-item-info > p {
  margin: 22px 0;
  font-size: 17px;
  line-height: 1.8;
  color: rgba(23, 20, 17, 0.76);
}

.product-item-subtitle {
  margin: 28px 0 12px;
  font-size: 18px;
  font-weight: 400;
}

.product-item-table {
  border-top: 1px solid rgba(23, 20, 17, 0.14);
}

.product-item-table div {
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  gap: 20px;
  padding: 13px 0;
  border-bottom: 1px solid rgba(23, 20, 17, 0.14);
}

.product-item-table span,
.product-item-table strong {
  display: block;
}

.product-item-table span {
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(23, 20, 17, 0.48);
}

.product-item-table strong {
  font-size: 17px;
  font-weight: 400;
  line-height: 1.4;
}

.product-item-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
}

.product-item-link {
  min-width: 140px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0;
  padding: 0 18px;
  background: #171411;
  color: #f5f3ef;
}

.product-item-link--secondary {
  background: transparent;
  color: #171411;
  border: 1px solid rgba(23, 20, 17, 0.22);
}

.product-preview {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 76px 86px;
  background: rgba(8, 8, 8, 0.92);
}

.product-preview img {
  max-width: min(1120px, 100%);
  max-height: 100%;
  display: block;
  object-fit: contain;
}

.product-preview__close,
.product-preview__nav {
  position: absolute;
  border: 1px solid rgba(255, 255, 255, 0.36);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.product-preview__close {
  top: 24px;
  right: 24px;
  height: 40px;
  padding: 0 18px;
}

.product-preview__nav {
  top: 50%;
  min-width: 64px;
  height: 42px;
  transform: translateY(-50%);
}

.product-preview__nav--prev {
  left: 24px;
}

.product-preview__nav--next {
  right: 24px;
}

@media (max-width: 1100px) {
  .product-item-layout {
    grid-template-columns: 1fr;
    gap: 36px;
  }

  .product-item-table div {
    grid-template-columns: 140px minmax(0, 1fr);
  }
}

@media (max-width: 720px) {
  .product-item-section {
    padding: 56px 0 68px;
  }

  .product-item-layout {
    gap: 30px;
  }

  .product-item-info > p {
    margin: 18px 0;
    font-size: 15px;
    line-height: 1.75;
  }

  .product-item-subtitle {
    margin-top: 24px;
  }

  .product-item-table div {
    grid-template-columns: 1fr;
    gap: 4px;
    padding: 12px 0;
  }

  .product-item-table strong {
    font-size: 15px;
  }

  .product-item-actions {
    flex-direction: column;
  }

  .product-item-actions .product-item-link {
    width: 100%;
    height: 48px;
  }

  .product-gallery__thumbs {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 10px;
    -webkit-overflow-scrolling: touch;
  }

  .product-gallery__thumb {
    flex: 0 0 64px;
  }

  .product-gallery__nav {
    top: auto;
    bottom: 14px;
    min-width: 48px;
    height: 34px;
    transform: none;
  }

  .product-gallery__pagination {
    bottom: 58px;
  }

  .product-preview {
    padding: 70px 16px 24px;
  }

  .product-preview__nav {
    top: auto;
    bottom: 22px;
    transform: none;
  }
}

@media (max-width: 480px) {
  .product-gallery__nav {
    font-size: 11px;
  }

  .product-preview__close {
    top: 18px;
    right: 16px;
  }

  .product-preview__nav--prev {
    left: 16px;
  }

  .product-preview__nav--next {
    right: 16px;
  }
}
</style>
